import React from 'react'
import PropTypes from 'prop-types'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import { PGA_COACH_PERSONAS } from '@lib/pga-coach/constants'
import { useStyles } from './PopularGoals.styles'

/**
 * Goal-oriented chip labels surfaced on the directory. Different from
 * {@link PGA_COACH_PERSONAS}.label, which is shorter for menu/tab contexts.
 * Mapping is keyed by persona id so the underlying taxonomy can stay shared.
 *
 * `play-better` is intentionally omitted from the legacy directory — it
 * overlaps with `break-100` (both surface improvement-oriented refinements)
 * and the chip rail design tightens to five clearly distinct goals.
 */
export const POPULAR_GOAL_CHIP_LABELS = {
  'start-golf': 'New to golf',
  'break-100': 'Lower my scores',
  juniors: 'Junior programs',
  'womens-golf': "Women's coaching",
  'military-veterans': 'Military & veterans',
}

export const POPULAR_GOAL_CHIPS = PGA_COACH_PERSONAS
  .filter((p) => POPULAR_GOAL_CHIP_LABELS[p.id])
  .map((p) => ({ id: p.id, label: POPULAR_GOAL_CHIP_LABELS[p.id] }))

export const POPULAR_GOALS_HEADING = 'What are you looking to accomplish?'

function PopularGoals({ selectedPersonaId, onPersonaChange }) {
  const classes = useStyles()

  const handleChipClick = (chipId) => {
    if (chipId === selectedPersonaId) {
      onPersonaChange(null, selectedPersonaId)
      return
    }
    onPersonaChange(chipId, selectedPersonaId || null)
  }

  return (
    <Box className={classes.root}>
      <Typography variant="h6" className={classes.heading}>
        {POPULAR_GOALS_HEADING}
      </Typography>
      <Box
        className={classes.rail}
        role="group"
        aria-label={POPULAR_GOALS_HEADING}
      >
        {POPULAR_GOAL_CHIPS.map((chip) => {
          const isSelected = chip.id === selectedPersonaId
          return (
            <Button
              key={chip.id}
              type="button"
              disableElevation
              aria-pressed={isSelected}
              data-persona={chip.id}
              className={[classes.chip, isSelected && classes.chipSelected].filter(Boolean).join(' ')}
              onClick={() => handleChipClick(chip.id)}
            >
              {chip.label}
            </Button>
          )
        })}
      </Box>
    </Box>
  )
}

PopularGoals.propTypes = {
  selectedPersonaId: PropTypes.string,
  onPersonaChange: PropTypes.func.isRequired,
}

PopularGoals.defaultProps = {
  selectedPersonaId: null,
}

export default PopularGoals
